import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, X } from "lucide-react";
import { ArrowLeft } from "../AdminIcons";
import FormSelect from "../common/FormSelect";
import Loader from "../common/Loader";
import {
  useDeleteCourseLearningOutcomePermanentlyMutation,
  useDeleteCourseModulePermanentlyMutation,
  useDeletePdfMaterialPermanentlyMutation,
  useGetAllDeletionHistoryQuery,
  useRestoreCourseLearningOutcomeMutation,
  useRestoreCourseModuleMutation,
  useRestoreCategoryMutation,
  useRestorePdfMaterialMutation,
  useRestoreLectureMutation,
  useRestoreRoleMutation,
} from "../../services/deletion-history/deletionHistoryApi";
import {
  useDeleteCourseLecturePermanentlyMutation,
  useRestoreCourseMutation,
} from "../../services/course/courseApi";
import {
  useRestoreStaffWithPendingStatusMutation,
  useDeleteStaffPermanentlyMutation,
} from "../../services/user/userApi";
import { getApiErrorMessage, showToast } from "../../utils/toast";
import { useDeleteCategoryPermanentlyMutation } from "../../services/categories/categoriesApi";

const TYPE_OPTIONS = [
  { label: "All", value: "All" },
  { label: "Staff", value: "Staff" },
  { label: "Role", value: "Role" },
  { label: "Category", value: "Category" },
  { label: "Course", value: "Course" },
  { label: "Course Module", value: "CourseModule" },
  { label: "Course Lecture", value: "CourseLecture" },
  { label: "PDF Material", value: "PdfMaterial" },
  { label: "Learning Outcome", value: "CourseLearningOutcome" },
];

const DATE_RANGE_OPTIONS = [
  { label: "All Dates", value: "" },
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
];

const getDateRangeBounds = (preset) => {
  if (!preset) {
    return { startDate: "", endDate: "" };
  }

  const now = new Date();
  const endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999);

  const startDate = new Date(now);
  startDate.setHours(0, 0, 0, 0);

  if (preset === "This Week") {
    const day = startDate.getDay();
    const daysFromMonday = day === 0 ? 6 : day - 1;
    startDate.setDate(startDate.getDate() - daysFromMonday);
  } else if (preset === "This Month") {
    startDate.setDate(1);
  } else if (preset !== "Today") {
    return { startDate: "", endDate: "" };
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

const DeletionHistoryView = () => {
  const [selectedType, setSelectedType] = useState("All");
  const [selectedDateRange, setSelectedDateRange] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { startDate, endDate } = useMemo(
    () => getDateRangeBounds(selectedDateRange),
    [selectedDateRange],
  );
  const currentDateLabel = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const queryParams = useMemo(
    () => ({
      type: selectedType || "All",
      startDate,
      endDate,
      page: currentPage,
    }),
    [selectedType, startDate, endDate, currentPage],
  );

  const navigate = useNavigate();

  const {
    currentData: deletionHistoryResponse,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetAllDeletionHistoryQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });
  const [deleteCourseModulePermanently, { isLoading: isDeleting }] =
    useDeleteCourseModulePermanentlyMutation();
  const [
    deleteCourseLearningOutcomePermanently,
    { isLoading: isDeletingLearningOutcome },
  ] = useDeleteCourseLearningOutcomePermanentlyMutation();
  const [deletePdfMaterialPermanently, { isLoading: isDeletingPdf }] =
    useDeletePdfMaterialPermanentlyMutation();
  const [deleteCategoryPermanently, {isLoading: isDeletingCategory}] = useDeleteCategoryPermanentlyMutation();
  const [deleteCourseLecturePermanently, { isLoading: isDeletingLecture }] =
    useDeleteCourseLecturePermanentlyMutation();
  const [deleteStaffPermanently, { isLoading: isDeletingStaff }] =
    useDeleteStaffPermanentlyMutation();
  const [restoreCourseModule, { isLoading: isRestoring }] =
    useRestoreCourseModuleMutation();
  const [restoreCourseLearningOutcome, { isLoading: isRestoringOutcome }] =
    useRestoreCourseLearningOutcomeMutation();
  const [restoreCategory, { isLoading: isRestoringCategory }] =
    useRestoreCategoryMutation();
  const [restorePdfMaterial, { isLoading: isRestoringPdf }] =
    useRestorePdfMaterialMutation();
  const [restoreLecutreMaterial, { isLoading: isRestoringLecture }] =
    useRestoreLectureMutation();
  const [restoreRole, { isLoading: isRestoringRole }] =
    useRestoreRoleMutation();
  const [restoreStaffWithPendingStatus, { isLoading: isRestoringStaff }] =
    useRestoreStaffWithPendingStatusMutation();
  const [restoreCourse, { isLoading: isRestoringCourse }] =
    useRestoreCourseMutation();

  const closeDeleteModal = () => setDeleteTarget(null);
  const closeRestoreModal = () => setRestoreTarget(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType, selectedDateRange]);

  const handleConfirmDelete = async () => {
    const id = deleteTarget?.itemId;
    if (!id) {
      closeDeleteModal();
      return;
    }

    try {
      let response;

      if (deleteTarget?.itemModel === "CourseLearningOutcome") {
        response = await deleteCourseLearningOutcomePermanently({
          id,
        }).unwrap();
      } else if (deleteTarget?.itemModel === "Category") {
        response = await deleteCategoryPermanently(id).unwrap();
      } else if (deleteTarget?.itemModel === "CourseLecture") {
        response = await deleteCourseLecturePermanently({
          lectureId: id,
        }).unwrap();
      } else if (deleteTarget?.itemModel === "PdfMaterial") {
        response = await deletePdfMaterialPermanently({ id }).unwrap();
      } else if (deleteTarget?.itemModel === "Staff") {
        response = await deleteStaffPermanently(id).unwrap();
      } else {
        response = await deleteCourseModulePermanently({ id }).unwrap();
      }

      if (response?.success === false) {
        showToast({
          type: "error",
          message: response?.message || "Failed to permanently delete item.",
        });
        return;
      }

      showToast({
        type: "success",
        message: response?.message || "Item permanently deleted successfully.",
      });
      closeDeleteModal();
      refetch();
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(
          error,
          "Failed to permanently delete item.",
        ),
      });
    }
  };

  const handleConfirmRestore = async () => {
    const id = restoreTarget?.itemId;
    if (!id) {
      closeRestoreModal();
      return;
    }

    try {
      let response;

      if (restoreTarget?.itemModel === "CourseLearningOutcome") {
        response = await restoreCourseLearningOutcome({ id }).unwrap();
      } else if (restoreTarget?.itemModel === "Course") {
        response = await restoreCourse(id).unwrap();
      } else if (restoreTarget?.itemModel === "Category") {
        response = await restoreCategory({ id }).unwrap();
      } else if (restoreTarget?.itemModel === "PdfMaterial") {
        const itemId = restoreTarget?.itemId;
        const courseId = restoreTarget?.id;

        if (!courseId) {
          throw new Error("Missing courseId for PdfMaterial restore");
        }

        response = await restorePdfMaterial({ id: courseId, itemId }).unwrap();
      } else if (restoreTarget?.itemModel === "CourseLecture") {
        response = await restoreLecutreMaterial({ lectureId: id }).unwrap();
      } else if (restoreTarget?.itemModel === "Role") {
        response = await restoreRole({ id }).unwrap();
      } else if (restoreTarget?.itemModel === "Staff") {
        const email = restoreTarget?.email || restoreTarget?.itemName;
        if (!email) {
          showToast({
            type: "error",
            message: "Missing email for staff restore.",
          });
          return;
        }

        response = await restoreStaffWithPendingStatus({
          email,
          staffId: id,
        }).unwrap();
      } else {
        response = await restoreCourseModule({ courseModuleId: id }).unwrap();
      }

      if (response?.success === false) {
        showToast({
          type: "error",
          message: response?.message || "Failed to restore item.",
        });
        return;
      }

      showToast({
        type: "success",
        message: response?.message || "Item restored successfully.",
      });
      closeRestoreModal();
      refetch();
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(error, "Failed to restore item."),
      });
    }
  };

  const formatDeletedBy = (performedBy) => {
    if (!performedBy) return "-";
    const fullName =
      `${performedBy.firstName || ""} ${performedBy.lastName || ""}`.trim();
    return fullName || performedBy.email || "-";
  };

  const formatType = (itemModel) => {
    if (!itemModel) return "-";
    return itemModel.replace(/([a-z])([A-Z])/g, "$1 $2");
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-US");
  };

  const noDataFromApi =
    deletionHistoryResponse?.success === false ||
    (isError &&
      typeof error?.data?.message === "string" &&
      error.data.message.toLowerCase().includes("no deletion history"));

  const normalizedData = noDataFromApi
    ? []
    : Array.isArray(deletionHistoryResponse?.data)
      ? deletionHistoryResponse.data
      : [];

  const apiItems = normalizedData
    .map((item) => ({
      id: item?._id || "",
      itemName: item?.itemName || "-",
      deletionDate: formatDate(item?.createdAt || item?.updatedAt),
      deletedBy: formatDeletedBy(item?.performedBy),
      itemModel: item?.itemModel || "",
      type: formatType(item?.itemModel),
      itemId: item?.itemId || "",
      email:
        item?.email ||
        item?.itemEmail ||
        item?.staffEmail ||
        item?.metadata?.email ||
        item?.itemName ||
        "",
    }))
    .filter((item) => item.id && item.itemId);

  const paginatedItems = apiItems;
  const totalPages = noDataFromApi
    ? 1
    : deletionHistoryResponse?.pagination?.totalPages || 1;
  const shouldShowRows = !isLoading && !isFetching && !noDataFromApi;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <section className="min-h-screen px-2 pt-4 2xl:pb-12 sm:px-4 xl:pl-8 xl:pr-7">
      <div className="px-4 pt-4 2xl:px-6 2xl:pt-7">
        <button
          type="button"
          aria-label="Go back"
          className="text-[#4A4A68]"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft />
        </button>

        <div className="mt-4 xl:mt-6 flex flex-wrap items-center justify-between gap-4 font-['Inter'] 2xl:mt-8 2xl:px-[14px]">
          <h2 className="text-xl sm:text-2xl  font-bold   text-[#111827]">
            Deletion History
          </h2>

          <p className="text-sm sm:text-base text-[#4A4A68]">
            {currentDateLabel} - Today
          </p>
        </div>

        <div className="mt-2 sm:mt-4 xl:mt-6 flex flex-wrap items-center gap-5 sm:gap-10 2xl:mt-8 2xl:px-[14px]">
          <h3 className="sm:text-xl font-bold leading-7 text-[#923D8C] 2xl:text-2xl">
            Deleted Items
          </h3>

          <div className="flex items-center gap-4">
            <FormSelect
              id="deletion-history-type"
              options={TYPE_OPTIONS}
              defaultValue={selectedType}
              placeholder="Type"
              onChange={setSelectedType}
              className="w-[140px]"
              selectClassName="rounded-full border border-[#938A96] bg-white px-[14px] py-1.5 text-xs text-[#4A4A68]"
              dropdownClassName="left-0 right-auto mt-1 min-w-[180px] rounded-md border border-[#E5E7EB] bg-white py-0.5 shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
              optionClassName="px-2 py-1.5 text-sm text-[#374151] hover:bg-[#F3F4F6]"
            />

            <FormSelect
              id="deletion-history-date-range"
              options={DATE_RANGE_OPTIONS}
              defaultValue={selectedDateRange}
              placeholder="Date Range"
              onChange={setSelectedDateRange}
              className="w-[130px]"
              selectClassName="rounded-full border border-[#938A96] bg-white px-[14px] py-1.5 text-xs text-[#4A4A68]"
              dropdownClassName="left-0 right-auto mt-1 min-w-[130px] rounded-md border border-[#E5E7EB] bg-white py-0.5 shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
              optionClassName="px-2 py-1.5 text-sm text-[#374151] hover:bg-[#F3F4F6]"
            />
          </div>
        </div>

        <div className="mt-8 xl:mt-12 bg-white">
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full min-w-[860px] table-fixed border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#EDE7F0] text-left text-sm font-medium text-[#000] [&>th]:py-5 2xl:[&>th]:py-7">
                  <th className="w-10 pl-3 pr-2 text-center sm:pl-10 sm:pr-5">
                    <input
                      type="checkbox"
                      aria-label="Select all deleted items"
                      className="h-[14px] w-[14px] accent-[#BC5BAE]"
                    />
                  </th>
                  <th className="w-32 px-2">Select all</th>
                  <th className="w-64 px-2">Item Name</th>
                  <th className="px-2">Deletion Date</th>
                  <th className="px-2">Deleted By</th>
                  <th className="px-2">Type</th>
                  <th className="px-2">Actions</th>
                </tr>
              </thead>

              <tbody>
                {isLoading || isFetching ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-2 py-7 text-center text-[#4A4A68]"
                    >
                      <div className="flex items-center justify-center">
                        <Loader size="lg" className="text-[#923D8C]" />
                      </div>
                    </td>
                  </tr>
                ) : null}
                {!isLoading &&
                !isFetching &&
                (noDataFromApi || paginatedItems.length === 0) ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-2 py-7 text-center text-[#4A4A68]"
                    >
                      No deletion history found.
                    </td>
                  </tr>
                ) : null}
                {shouldShowRows &&
                  paginatedItems.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`text-base text-[#000] [&>td]:py-5 2xl:[&>td]:py-7 ${
                        index !== paginatedItems.length - 1
                          ? "border-b border-[#EFEFFD]"
                          : ""
                      }`}
                    >
                      <td className="w-20 pl-3 text-center sm:pl-14">
                        <input
                          type="checkbox"
                          aria-label={`Select ${item.itemName}`}
                          className="h-[14px] w-[14px] accent-[#BC5BAE]"
                        />
                      </td>
                      <td className="w-28 px-2"></td>
                      <td className="w-64 px-2">{item.itemName}</td>
                      <td className="px-2">{item.deletionDate}</td>
                      <td className="px-2">{item.deletedBy}</td>
                      <td className="px-2">{item.type}</td>
                      <td className="px-2">
                        <div className="flex items-center gap-5">
                          <button
                            type="button"
                            className="text-[#007AFF] underline underline-offset-2"
                            onClick={() => setRestoreTarget(item)}
                          >
                            Restore
                          </button>

                          {item.itemModel !== "Role" &&
                            item.itemModel !== "Course" && (
                              <button
                                type="button"
                                aria-label={`Permanently delete ${item.itemName}`}
                                className="text-[#111827]"
                                onClick={() => setDeleteTarget(item)}
                              >
                                <Trash2 size={24} />
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-6 py-4">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
              className={`rounded-full px-6 py-1 text-sm ${
                currentPage === 1
                  ? "cursor-not-allowed bg-[#D9D9D9] text-[#6B7280]"
                  : "bg-[#923D8C] text-white"
              }`}
            >
              Prev
            </button>

            <p className="text-base font-medium text-black">
              Page {currentPage}/{totalPages}
            </p>

            <button
              type="button"
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
              disabled={currentPage === totalPages}
              className={`rounded-full px-6 py-1 text-sm ${
                currentPage === totalPages
                  ? "cursor-not-allowed bg-[#D9D9D9] text-[#6B7280]"
                  : "bg-[#923D8C] text-white"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {deleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1F1B24]/70 px-4">
          <div className="relative w-full max-w-[447px] rounded-[19px] bg-white px-8 pb-10 pt-6">
            <button
              type="button"
              aria-label="Close delete confirmation"
              className="absolute right-8 top-5 text-[#151515]"
              onClick={closeDeleteModal}
            >
              <X size={30} strokeWidth={2.4} />
            </button>

            <p className="pt-10 text-center text-base text-black">
              Do you want to permanently delete this ?
            </p>

            <div className="mt-5 flex items-center justify-center gap-10">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="min-w-[99px] rounded-full bg-[#D9D9D9] tracking-[3%] px-6 py-1.5 text-[15px] text-black"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={
                  isDeleting ||
                  isDeletingStaff ||
                  isDeletingCategory ||
                  isDeletingLearningOutcome ||
                  isDeletingPdf ||
                  isDeletingLecture
                }
                className="min-w-[99px] rounded-full bg-[#923D8C] px-6 py-1.5 text-[15px] tracking-[3%] text-white flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isDeleting ||
                isDeletingStaff ||
                isDeletingCategory ||
                isDeletingLearningOutcome ||
                isDeletingPdf ||
                isDeletingLecture ? (
                  <>
                    <Loader size="sm" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {restoreTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1F1B24]/70 px-4">
          <div className="relative w-full max-w-[447px] rounded-[19px] bg-white px-8 pb-10 pt-6">
            <button
              type="button"
              aria-label="Close restore confirmation"
              className="absolute right-8 top-5 text-[#151515]"
              onClick={closeRestoreModal}
            >
              <X size={30} strokeWidth={2.4} />
            </button>

            <p className="pt-10 text-center text-base text-black">
              Do you want to restore this item?
            </p>

            <div className="mt-5 flex items-center justify-center gap-10">
              <button
                type="button"
                onClick={closeRestoreModal}
                className="min-w-[99px] rounded-full bg-[#D9D9D9] tracking-[3%] px-6 py-1.5 text-[15px] text-black"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmRestore}
                disabled={
                  isRestoring ||
                  isRestoringOutcome ||
                  isRestoringCategory ||
                  isRestoringPdf ||
                  isRestoringLecture ||
                  isRestoringRole ||
                  isRestoringStaff ||
                  isRestoringCourse
                }
                className="min-w-[99px] rounded-full bg-[#923D8C] px-6 py-1.5 text-[15px] tracking-[3%] text-white"
              >
                {isRestoring ||
                isRestoringOutcome ||
                isRestoringCategory ||
                isRestoringPdf ||
                isRestoringLecture ||
                isRestoringRole ||
                isRestoringStaff ||
                isRestoringCourse
                  ? "Restoring..."
                  : "Restore"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default DeletionHistoryView;
